// Part of salesPages, split by screen to keep files short; ../salesPages.ts merges the parts.
// Keys sit directly under salesPages, in sub-objects whose names no other part uses.
// Customers, vendors, products and services, one project, inventory quantity adjustment.
export const en = {
  // Shared by the customer, vendor and product lists and the contact page.
  recordList: {
    status: 'Status',
    active: 'Active',
    all: 'All',
    action: 'Action',
    view: 'View',
    moreActions: 'More actions for {{name}}',
    makeInactive: 'Make inactive',
    makeActive: 'Make active',
    activeAgain: '{{name}} is active again.',
    nowInactive: '{{name}} is now inactive.',
    inactiveTitle: 'Make {{name}} inactive?',
  },
  contactList: {
    newContact: { customer: 'New customer', vendor: 'New vendor' },
    searchPlaceholder: 'Search by name, company, email, phone or {{id}}',
    searchLabel: { customer: 'Search customers', vendor: 'Search vendors' },
    count: {
      customer_one: '{{count}} customer',
      customer_other: '{{count}} customers',
      vendor_one: '{{count}} vendor',
      vendor_other: '{{count}} vendors',
    },
    openBalanceLower: 'open balance',
    loadError: { customer: 'Could not load customers', vendor: 'Could not load vendors' },
    noneYet: { customer: 'No customers yet', vendor: 'No vendors yet' },
    noMatch: {
      customer: 'No customers match your search',
      vendor: 'No vendors match your search',
    },
    colName: { customer: 'Customer / Company', vendor: 'Vendor / Company' },
    colPhone: 'Phone',
    colEmail: 'Email',
    colOpenBalance: 'Open balance ({{currency}})',
    createInvoice: 'Create invoice',
    createBill: 'Create bill',
    createStatement: 'Create statement',
    footnote: {
      customer: 'Open balance is the accounts receivable balance posted against each customer.',
      vendor: 'Open balance is the accounts payable balance posted against each vendor.',
    },
    inactiveBody: {
      customer:
        'Inactive customers are hidden from lists and pickers. Their transactions stay in reports. A customer with an open balance must be settled first.',
      vendor:
        'Inactive vendors are hidden from lists and pickers. Their transactions stay in reports. A vendor with an open balance must be settled first.',
    },
  },
  contactDetail: {
    notFound: { customer: 'Customer not found', vendor: 'Vendor not found' },
    customerAndVendor: 'Customer and vendor',
    taxRegistered: '{{tax}} registered',
    openBalance: 'Open balance',
    newTransaction: 'New transaction',
    menu: {
      invoice: 'Invoice',
      receivePayment: 'Receive payment',
      quote: 'Quote',
      creditNote: 'Credit note',
      salesReceipt: 'Sales receipt',
      bill: 'Bill',
      expense: 'Expense',
      payBills: 'Pay bills',
      debitNote: 'Debit note',
      journalEntry: 'Journal entry',
    },
    transactionList: 'Transaction list',
    detailsTab: { customer: 'Customer details', vendor: 'Vendor details' },
    type: 'Type',
    filter: {
      all: 'All transactions',
      invoice: 'Invoices',
      payment: 'Payments',
      credit_note: 'Credit notes',
      sales_receipt: 'Sales receipts',
      quote: 'Quotes',
      bill: 'Bills',
      expense: 'Expenses',
      vendor_payment: 'Bill payments',
      debit_note: 'Debit notes',
      journal: 'Journal entries',
    },
    loadError: 'Could not load transactions',
    noMatch: 'No transactions match these filters',
    noneYet: 'No transactions yet',
    customerHint:
      'Invoices, payments, credit notes, sales receipts and quotes for this customer appear here.',
    vendorHint:
      'Bills, expenses, bill payments and debit notes for this vendor appear here. Journal entries naming the vendor are under Journal entries.',
    journalHint: 'Transactions that name this contact appear here.',
    colDate: 'Date',
    colType: 'Type',
    colNo: 'No.',
    colAccount: 'Account',
    colMemo: 'Memo / description',
    colDebit: 'Debit',
    colCredit: 'Credit',
    colStatus: 'Status',
    reversed: 'Reversed',
    posted: 'Posted',
    totalLines_one: 'Total ({{count}} lines)',
    totalLines_other: 'Total ({{count}} lines)',
    field: {
      displayName: 'Display name',
      companyName: 'Company name',
      contactName: 'Contact name',
      email: 'Email',
      phone: 'Phone',
      mobile: 'Mobile',
      website: 'Website',
      billingAddress: 'Billing address',
      address: 'Address',
      shippingAddress: 'Shipping address',
      sameAsBilling: 'Same as billing address',
      country: 'Country',
      placeOfSupply: 'Place of supply',
      gstRegistered: 'GST registered',
      defaultGstRate: 'Default GST rate',
      vatRegistered: 'VAT registered',
      defaultVatTreatment: 'Default VAT treatment',
      terms: 'Terms',
      defaultCategory: 'Default category',
      bankName: 'Bank name',
      accountIfsc: 'Account number and IFSC',
      iban: 'IBAN',
      currency: 'Currency',
      notes: 'Notes',
      added: 'Added',
    },
  },
  itemList: {
    title: 'Products and services',
    newItem: 'New product/service',
    searchPlaceholder: 'Search by name, SKU or description',
    searchLabel: 'Search products and services',
    type: 'Type',
    allTypes: 'All types',
    count_one: '{{count}} item',
    count_other: '{{count}} items',
    loadError: 'Could not load products and services',
    noneYet: 'No products or services yet',
    noMatch: 'No products or services match your filters',
    emptyHint: 'Add what this client sells and buys so invoice and bill lines fill in themselves.',
    colName: 'Name',
    colSku: 'SKU',
    colType: 'Type',
    colSalesDescription: 'Sales description',
    colSalesPrice: 'Sales price',
    colIncomeAccount: 'Income account',
    colCost: 'Cost',
    colExpenseAccount: 'Expense account',
    colQtyOnHand: 'Qty on hand',
    lowStock: 'Low stock',
    duplicate: 'Duplicate',
    adjustQuantity: 'Adjust quantity',
    footnote:
      'Prices are in {{currency}}. Inventory items keep a quantity on hand at FIFO cost; count the shelf with Adjust quantity.',
    inactiveBody:
      'Inactive products and services are hidden from lists and pickers. Transactions that use them keep their lines. You can make them active again from the Inactive filter.',
  },
  projectView: {
    loadError: 'Could not open the project',
    fromDate: 'from {{date}}',
    toDate: 'to {{date}}',
    profitAndLoss: 'Profit and loss',
    income: 'Income',
    costs: 'Costs',
    profit: 'Profit',
    margin: 'Margin',
    openBalance: 'Open balance',
    transactions: 'Transactions',
    noneYet: 'No transactions yet',
    noneYetHint: 'Choose this project on an invoice, bill, expense or journal entry.',
    colDate: 'Date',
    colType: 'Type',
    colNo: 'No.',
    colName: 'Name',
    colMemo: 'Memo',
    colAmount: 'Amount',
  },
  inventoryAdjust: {
    title: 'Inventory quantity adjustment',
    adjusted: 'Inventory adjusted: {{list}}.',
    listSeparator: ', ',
    adjustmentDate: 'Adjustment date *',
    account: 'Inventory adjustment account *',
    accountHint: 'An expense, such as Inventory shrinkage.',
    chooseAccount: 'Choose an account',
    reference: 'Reference no.',
    lines: 'Adjustment lines',
    colProduct: 'Product',
    colQtyOnHand: 'Qty on hand',
    colNewQty: 'New qty',
    colChange: 'Change in qty',
    productLine: 'Product, line {{n}}',
    newQuantityLine: 'New quantity, line {{n}}',
    deleteLine: 'Delete line {{n}}',
    addLines: 'Add lines',
    memo: 'Memo',
  },
};

export const ar = {
  recordList: {
    status: 'الحالة',
    active: 'نشط',
    all: 'الكل',
    action: 'الإجراء',
    view: 'عرض',
    moreActions: 'إجراءات أخرى لـ {{name}}',
    makeInactive: 'تعطيل',
    makeActive: 'تفعيل',
    activeAgain: 'تمت إعادة تفعيل {{name}}.',
    nowInactive: 'تم تعطيل {{name}}.',
    inactiveTitle: 'هل تريد تعطيل {{name}}؟',
  },
  contactList: {
    newContact: { customer: 'عميل جديد', vendor: 'مورد جديد' },
    searchPlaceholder: 'ابحث بالاسم أو الشركة أو البريد الإلكتروني أو الهاتف أو {{id}}',
    searchLabel: { customer: 'البحث في العملاء', vendor: 'البحث في الموردين' },
    count: {
      customer_zero: 'لا يوجد عملاء',
      customer_one: 'عميل واحد',
      customer_two: 'عميلان',
      customer_few: '{{count}} عملاء',
      customer_many: '{{count}} عميلًا',
      customer_other: '{{count}} عميل',
      vendor_zero: 'لا يوجد موردون',
      vendor_one: 'مورد واحد',
      vendor_two: 'موردان',
      vendor_few: '{{count}} موردين',
      vendor_many: '{{count}} موردًا',
      vendor_other: '{{count}} مورد',
    },
    openBalanceLower: 'الرصيد المستحق',
    loadError: { customer: 'تعذر تحميل العملاء', vendor: 'تعذر تحميل الموردين' },
    noneYet: { customer: 'لا يوجد عملاء بعد', vendor: 'لا يوجد موردون بعد' },
    noMatch: {
      customer: 'لا يوجد عملاء يطابقون بحثك',
      vendor: 'لا يوجد موردون يطابقون بحثك',
    },
    colName: { customer: 'العميل / الشركة', vendor: 'المورد / الشركة' },
    colPhone: 'الهاتف',
    colEmail: 'البريد الإلكتروني',
    colOpenBalance: 'الرصيد المستحق ({{currency}})',
    createInvoice: 'إنشاء فاتورة',
    createBill: 'إنشاء فاتورة مورد',
    createStatement: 'إنشاء كشف حساب',
    footnote: {
      customer: 'الرصيد المستحق هو رصيد الذمم المدينة المرحّل على كل عميل.',
      vendor: 'الرصيد المستحق هو رصيد الذمم الدائنة المرحّل على كل مورد.',
    },
    inactiveBody: {
      customer:
        'يُخفى العملاء المعطَّلون من القوائم وقوائم الاختيار، وتبقى معاملاتهم في التقارير. يجب تسوية رصيد العميل المستحق قبل تعطيله.',
      vendor:
        'يُخفى الموردون المعطَّلون من القوائم وقوائم الاختيار، وتبقى معاملاتهم في التقارير. يجب تسوية رصيد المورد المستحق قبل تعطيله.',
    },
  },
  contactDetail: {
    notFound: { customer: 'العميل غير موجود', vendor: 'المورد غير موجود' },
    customerAndVendor: 'عميل ومورد',
    taxRegistered: 'مسجّل في {{tax}}',
    openBalance: 'الرصيد المستحق',
    newTransaction: 'معاملة جديدة',
    menu: {
      invoice: 'فاتورة',
      receivePayment: 'استلام دفعة',
      quote: 'عرض سعر',
      creditNote: 'إشعار دائن',
      salesReceipt: 'إيصال مبيعات',
      bill: 'فاتورة مورد',
      expense: 'مصروف',
      payBills: 'سداد فواتير الموردين',
      debitNote: 'إشعار مدين',
      journalEntry: 'قيد يومية',
    },
    transactionList: 'قائمة المعاملات',
    detailsTab: { customer: 'بيانات العميل', vendor: 'بيانات المورد' },
    type: 'النوع',
    filter: {
      all: 'كل المعاملات',
      invoice: 'الفواتير',
      payment: 'الدفعات',
      credit_note: 'الإشعارات الدائنة',
      sales_receipt: 'إيصالات المبيعات',
      quote: 'عروض الأسعار',
      bill: 'فواتير الموردين',
      expense: 'المصروفات',
      vendor_payment: 'دفعات فواتير الموردين',
      debit_note: 'الإشعارات المدينة',
      journal: 'قيود اليومية',
    },
    loadError: 'تعذر تحميل المعاملات',
    noMatch: 'لا توجد معاملات تطابق عوامل التصفية هذه',
    noneYet: 'لا توجد معاملات بعد',
    customerHint:
      'تظهر هنا الفواتير والدفعات والإشعارات الدائنة وإيصالات المبيعات وعروض الأسعار الخاصة بهذا العميل.',
    vendorHint:
      'تظهر هنا فواتير الموردين والمصروفات ودفعات الفواتير والإشعارات المدينة الخاصة بهذا المورد. أما قيود اليومية التي تذكر المورد فتجدها ضمن قيود اليومية.',
    journalHint: 'تظهر هنا المعاملات التي تذكر جهة الاتصال هذه.',
    colDate: 'التاريخ',
    colType: 'النوع',
    colNo: 'الرقم',
    colAccount: 'الحساب',
    colMemo: 'المذكرة / الوصف',
    colDebit: 'مدين',
    colCredit: 'دائن',
    colStatus: 'الحالة',
    reversed: 'معكوس',
    posted: 'مرحّل',
    totalLines_zero: 'الإجمالي (لا توجد أسطر)',
    totalLines_one: 'الإجمالي (سطر واحد)',
    totalLines_two: 'الإجمالي (سطران)',
    totalLines_few: 'الإجمالي ({{count}} أسطر)',
    totalLines_many: 'الإجمالي ({{count}} سطرًا)',
    totalLines_other: 'الإجمالي ({{count}} سطر)',
    field: {
      displayName: 'الاسم المعروض',
      companyName: 'اسم الشركة',
      contactName: 'اسم جهة الاتصال',
      email: 'البريد الإلكتروني',
      phone: 'الهاتف',
      mobile: 'الجوال',
      website: 'الموقع الإلكتروني',
      billingAddress: 'عنوان الفوترة',
      address: 'العنوان',
      shippingAddress: 'عنوان الشحن',
      sameAsBilling: 'مطابق لعنوان الفوترة',
      country: 'الدولة',
      placeOfSupply: 'مكان التوريد',
      gstRegistered: 'مسجّل في GST',
      defaultGstRate: 'نسبة GST الافتراضية',
      vatRegistered: 'مسجّل في VAT',
      defaultVatTreatment: 'معاملة VAT الافتراضية',
      terms: 'شروط الدفع',
      defaultCategory: 'الفئة الافتراضية',
      bankName: 'اسم البنك',
      accountIfsc: 'رقم الحساب وIFSC',
      iban: 'IBAN',
      currency: 'العملة',
      notes: 'ملاحظات',
      added: 'تاريخ الإضافة',
    },
  },
  itemList: {
    title: 'المنتجات والخدمات',
    newItem: 'منتج/خدمة جديدة',
    searchPlaceholder: 'ابحث بالاسم أو SKU أو الوصف',
    searchLabel: 'البحث في المنتجات والخدمات',
    type: 'النوع',
    allTypes: 'كل الأنواع',
    count_zero: 'لا توجد أصناف',
    count_one: 'صنف واحد',
    count_two: 'صنفان',
    count_few: '{{count}} أصناف',
    count_many: '{{count}} صنفًا',
    count_other: '{{count}} صنف',
    loadError: 'تعذر تحميل المنتجات والخدمات',
    noneYet: 'لا توجد منتجات أو خدمات بعد',
    noMatch: 'لا توجد منتجات أو خدمات تطابق عوامل التصفية',
    emptyHint: 'أضف ما يبيعه هذا العميل وما يشتريه لتُملأ أسطر الفواتير وفواتير الموردين تلقائيًا.',
    colName: 'الاسم',
    colSku: 'SKU',
    colType: 'النوع',
    colSalesDescription: 'وصف المبيعات',
    colSalesPrice: 'سعر البيع',
    colIncomeAccount: 'حساب الإيرادات',
    colCost: 'التكلفة',
    colExpenseAccount: 'حساب المصروفات',
    colQtyOnHand: 'الكمية المتوفرة',
    lowStock: 'مخزون منخفض',
    duplicate: 'نسخ',
    adjustQuantity: 'تسوية الكمية',
    footnote:
      'الأسعار بـ {{currency}}. تحتفظ أصناف المخزون بكمية متوفرة بتكلفة FIFO؛ اجرد الرف باستخدام تسوية الكمية.',
    inactiveBody:
      'تُخفى المنتجات والخدمات المعطَّلة من القوائم وقوائم الاختيار، وتحتفظ المعاملات التي تستخدمها بأسطرها. يمكنك إعادة تفعيلها من عامل التصفية «غير نشط».',
  },
  projectView: {
    loadError: 'تعذر فتح المشروع',
    fromDate: 'من {{date}}',
    toDate: 'إلى {{date}}',
    profitAndLoss: 'الأرباح والخسائر',
    income: 'الإيرادات',
    costs: 'التكاليف',
    profit: 'الربح',
    margin: 'هامش الربح',
    openBalance: 'الرصيد المستحق',
    transactions: 'المعاملات',
    noneYet: 'لا توجد معاملات بعد',
    noneYetHint: 'اختر هذا المشروع في فاتورة أو فاتورة مورد أو مصروف أو قيد يومية.',
    colDate: 'التاريخ',
    colType: 'النوع',
    colNo: 'الرقم',
    colName: 'الاسم',
    colMemo: 'المذكرة',
    colAmount: 'المبلغ',
  },
  inventoryAdjust: {
    title: 'تسوية كمية المخزون',
    adjusted: 'تمت تسوية المخزون: {{list}}.',
    listSeparator: '، ',
    adjustmentDate: 'تاريخ التسوية *',
    account: 'حساب تسوية المخزون *',
    accountHint: 'حساب مصروف، مثل عجز المخزون.',
    chooseAccount: 'اختر حسابًا',
    reference: 'الرقم المرجعي',
    lines: 'أسطر التسوية',
    colProduct: 'المنتج',
    colQtyOnHand: 'الكمية المتوفرة',
    colNewQty: 'الكمية الجديدة',
    colChange: 'التغير في الكمية',
    productLine: 'المنتج، السطر {{n}}',
    newQuantityLine: 'الكمية الجديدة، السطر {{n}}',
    deleteLine: 'حذف السطر {{n}}',
    addLines: 'إضافة أسطر',
    memo: 'المذكرة',
  },
};
